import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type DuplicateAssetsBatchBody, batchUpdateResponseSchema } from '@/types/batch';
import { batchUpdate, getBatchProgress } from '@/services/batch.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useBatchStore } from '@/hooks/data/batch/useBatchStore';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useBatchDuplicateAssets = () => {
  const tToasts = useTranslations('toasts');
  const queryClient = useQueryClient();

  const { toast } = useToast();
  const pollProgress = useBatchStore((state) => state.pollProgress);
  const markBatchErrored = useBatchStore((state) => state.markBatchErrored);

  const batchDuplicateAssetsWithAuth = useAuthenticatedQueryFn(batchUpdate);
  const getBatchProgressWithAuth = useAuthenticatedQueryFn(getBatchProgress);

  const batchDuplicateAssetsMutation = useMutation({
    mutationFn: async (body: DuplicateAssetsBatchBody) =>
      batchUpdateResponseSchema.parse(await batchDuplicateAssetsWithAuth(body)),
    onSuccess: async (response, { payload }) => {
      try {
        const batchId = response.batchId;

        await pollProgress(batchId, getBatchProgressWithAuth, 'Duplicate assets');
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.AssetData, { folderId: payload.destinationFolderId }],
        });

        toast({
          title: tToasts('batchDuplicateAssetsMutation.title'),
          description: tToasts('batchDuplicateAssetsMutation.description'),
        });
      } catch (err) {
        errorLog(err);
      }
    },
    onError: (err) => {
      markBatchErrored('Duplicate assets');
      errorLog(err);
    },
  });

  return {
    batchDuplicateAssetsMutation,
  };
};
